import { useCallback, useMemo, useState } from 'react';
import apiClient from '@/lib/api/client';
import { MmdlPlanViewer } from './MmdlPlanViewer';
import { SketchEngine3D, HelpContent } from './SketchEngine3D';
import type { EngineObject, EngineLayer, SketchData } from './SketchEngine3D';

// Viewer for MiTek SAPPHIRE / BC Framer .mmdl truss models.
// Defaults to an interactive 3D view (geometry parsed server-side from the
// model's primitives3d entry); Plan tab shows the embedded 2D layout; Help tab
// explains the controls. Designed for non-technical users on touch or mouse.
//
// CR #207 built the sketch layer (waves 1–7) in this file; CR #208 extracted
// it wholesale into SketchEngine3D so the drafting suite shares the exact
// same toolkit. This wrapper only supplies what is mmdl-specific: the
// geometry endpoint, the layer names, the per-document sketch endpoints, and
// the Plan tab. Model units are FEET (verified empirically: truss chords
// parse 0.13 ft = 1-1/2" thick; job 13's footprint is ~93 ft × 72 ft).

type Tab = '3d' | 'plan' | 'help';

const LAYER_DEFS: EngineLayer[] = [
  { key: 'Roof Truss', label: 'Trusses', swatch: '#8080f0' },
  { key: 'Solid Wall', label: 'Walls', swatch: '#d3d3d3' },
  { key: 'Beam', label: 'Beams', swatch: '#ee687b' },
  { key: 'Roof Accessory', label: 'Other', swatch: '#9a9a9a', catchAll: true },
];

export function MmdlViewer({ url, name, docId }: { url: string; name: string; docId?: number }) {
  const [tab, setTab] = useState<Tab>('3d');
  const [geoError, setGeoError] = useState('');

  const loadModel = useCallback(async (): Promise<EngineObject[]> => {
    const { data: resp } = await apiClient.get(`/mmdl/geometry?file=${encodeURIComponent(url)}`);
    return resp.data.objects as EngineObject[];
  }, [url]);

  // Shared per-document sketch (last write wins, everyone sees the same one).
  const adapter = useMemo(() => docId ? {
    load: async () => (await apiClient.get(`/mmdl/sketch?documentId=${docId}`)).data.data as unknown,
    save: async (data: SketchData) => { await apiClient.put(`/mmdl/sketch?documentId=${docId}`, { data }); },
  } : undefined, [docId]);

  const tabBtn = (t: Tab, label: string, icon: string) => (
    <button
      onClick={() => setTab(t)}
      className={`flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-semibold transition-colors ${
        tab === t ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
      }`}
    >
      <span className="material-symbols-outlined text-lg">{icon}</span>
      {label}
    </button>
  );

  return (
    <div className="w-full h-full flex flex-col">
      {/* Tabs */}
      <div className="flex items-center gap-2 pb-3 flex-wrap">
        {tabBtn('3d', '3D Model', 'view_in_ar')}
        {tabBtn('plan', 'Plan View', 'map')}
        {tabBtn('help', 'Help', 'help')}
        {geoError && <span className="text-xs text-amber-700">{geoError}</span>}
      </div>

      <div className="flex-1 min-h-0 relative">
        {tab === 'help' && <div className="absolute inset-0 overflow-y-auto bg-gray-50 rounded-lg"><HelpContent /></div>}
        {tab === 'plan' && <MmdlPlanViewer url={url} name={name} />}
        {/* Always mounted — the engine keeps the sketch and undo stacks in
            refs across tab switches (it renders null while inactive). */}
        <SketchEngine3D
          active={tab === '3d'}
          modelKey={url}
          loadModel={loadModel}
          layers={LAYER_DEFS}
          adapter={adapter}
          adapterKey={docId}
          onError={() => { setGeoError('3D view is not available for this file — showing the plan instead.'); setTab('plan'); }}
        />
      </div>
    </div>
  );
}
